<template>
  <div class="search-form">
    <h1 class="search-page__headline headline-large">
      {{ $t('search.title') }}
    </h1>
    <div class="search-form__wrapper">
      <div class="search-form__inner">
        <SearchOrigin />
        <SearchDestination />
        <DirectionButton />
      </div>
      <div class="search-form__calendar-group">
        <TravelDate />
        <ReturnDate />
      </div>
      <button
        class="search-form__button"
        :class="{ disabled: !canProceed }"
        :disabled="!canProceed"
        @click="handleSubmit">
        {{ $t('search.continue') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useTravelSearchStore } from '@/stores/travelSearchStore'
  import { fetchStations } from '@/services/journeyService'
  import { pushOrderFlowToDatalayer } from '@/services/gaLoggingService'
  import { Station } from '@/types/stationTypes'
  import { parseSearchPrefillQuery } from '@/utils/searchQueryPrefill'
  import SearchOrigin from '@/components/TravelPlanner/SearchOrigin.vue'
  import SearchDestination from '@/components/TravelPlanner/SearchDestination.vue'
  import TravelDate from '@/components/TravelPlanner/TravelDate.vue'
  import ReturnDate from '@/components/TravelPlanner/ReturnDate.vue'
  import DirectionButton from '@/components/TravelPlanner/DirectionButton.vue'

  // Store setup
  const travelSearchStore = useTravelSearchStore()

  const { OriginStation, destinationStation, departureDate, returnDate } = storeToRefs(travelSearchStore)

  const canProceed = computed(() => {
    return !!OriginStation.value?.name && !!destinationStation.value?.name && !!departureDate.value
  })

  const findStationByQueryValue = (
    stations: Station[],
    queryValue: string | undefined,
  ): Station | undefined => {
    if (!queryValue) return undefined

    const normalizedValue = queryValue.trim().toLowerCase()
    return stations.find(
      (station) =>
        station._u_i_c_station_code.toLowerCase() === normalizedValue ||
        station.name.toLowerCase() === normalizedValue,
    )
  }

  const applyQueryPrefill = async () => {
    const parsedQuery = parseSearchPrefillQuery(window.location.search)

    if (parsedQuery.errors.length) {
      parsedQuery.errors.forEach((error) => {
        console.warn(error)
      })
    }

    travelSearchStore.reset()

    if (!parsedQuery.hasPrefillValues) {
      return
    }

    if (parsedQuery.outboundDate) {
      travelSearchStore.setDepartureDate(parsedQuery.outboundDate)
    }

    travelSearchStore.setReturnDate(parsedQuery.inboundDate ?? null)

    if (!parsedQuery.origin && !parsedQuery.destination) {
      return
    }

    if (!travelSearchStore.allStations.length) {
      const fetchedStations = await fetchStations()
      travelSearchStore.setAllStations(fetchedStations)
    }

    const originStation = findStationByQueryValue(travelSearchStore.allStations, parsedQuery.origin)
    if (originStation) {
      await travelSearchStore.setOriginStation({
        name: originStation.name,
        _u_i_c_station_code: originStation._u_i_c_station_code,
      })
    }

    const destinationStation = findStationByQueryValue(
      travelSearchStore.allStations,
      parsedQuery.destination,
    )

    if (destinationStation) {
      travelSearchStore.setDestinationStation({
        name: destinationStation.name,
        _u_i_c_station_code: destinationStation._u_i_c_station_code,
      })
      travelSearchStore.setOriginDestination(
        travelSearchStore.OriginStation.name,
        destinationStation.name,
      )
    }
  }

  const handleSubmit = () => {
    const query: Record<string, string> = {
      origin: OriginStation.value?._u_i_c_station_code || OriginStation.value?.name || '',
      destination:
        destinationStation.value?._u_i_c_station_code || destinationStation.value?.name || '',
      outboundDate: departureDate.value || '',
      fromLight: 'true',
    }

    if (returnDate.value) {
      query.returnDate = returnDate.value
    }

    // Redirect to SPA
    const queryString = new URLSearchParams(query).toString()
    const spaBaseUrl = import.meta.env.VITE_SPA_BASE_URL
    window.location.href = `${spaBaseUrl}/?${queryString}`
  }

  onMounted(async () => {
    try {
      await applyQueryPrefill()
      pushOrderFlowToDatalayer(1, 'Start search', { order_type: 'New order' })
    } catch (error) {
      console.error('Error initializing SearchForm:', error)
    }
  })
</script>

<style scoped lang="scss">
  .search-form {
    width: 100%;
    max-width: max-content;
    margin: 0 auto;
    box-sizing: border-box;
  }

  .search-page__headline {
    margin-bottom: 0.67em;
  }

  .search-form__wrapper {
    position: relative;
    width: min-content;
    max-width: max-content;
    margin: 0 auto;
    background-color: $clr-white;
    padding: 24px;
    color: #333;
    box-shadow: $box-shadow-card;
    border-radius: $border-radius;
    border: 1px solid lightgray;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    box-sizing: border-box;

    @media (min-width: $breakpoint-sm) {
      max-width: 400px;
    }

    @media (min-width: $breakpoint-xl) {
      flex-direction: row;
      align-items: center;
      max-width: max-content;
    }

    & > * {
      flex: 1 1 auto;
    }

    .search-form__inner {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 12px;

      @media (min-width: $breakpoint-xl) {
        flex-direction: row;
      }

      & > * {
        flex: 1 1 auto;
      }
    }

    .search-form__calendar-group {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      flex: 1 1 auto;
      gap: 12px;

      & > * {
        flex: 1;
      }

      @media (min-width: $breakpoint-xl) {
        gap: 16px;

        & > * {
          flex: 1;
        }
      }
    }

    .search-form__button {
      padding: rem(20) 40px;
      background-color: $clr-primary;
      color: $clr-white;
      border: none;
      border-radius: $border-radius;
      cursor: pointer;
      font-size: rem(16);
      line-height: rem(24);
      text-align: center;

      &.disabled {
        @include button-disabled;
      }
    }
  }
</style>
